import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Pause, Play, Square, Zap } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { ApprovalMode, FlowView, ProspectDetail } from "@/lib/types";
import { Card, CardTitle } from "@/components/Card";
import { formatDateTime } from "@/lib/format";

type FlowAction = "start" | "pause" | "resume" | "stop" | "run-now";

/**
 * How long "Run now" stays disabled after a click. The tick runs
 * asynchronously, so the button has nothing to wait on; without a cooldown the
 * operator sees no change, clicks again, and every extra click is either a
 * no-op or a second request racing the first.
 */
export const RUN_NOW_COOLDOWN_MS = 10_000;

/**
 * When to refetch after a queued tick. The step composes through an LLM call,
 * so the first refetch catches a fast one and the second the usual case.
 */
const REFRESH_AFTER_TICK_MS = [4_000, 12_000];

/**
 * Start, pause and stop the agent on one prospect, and choose whether its
 * messages need a human.
 *
 * <p>This card is the answer to "how do I begin outreach for this prospect".
 * Before it existed the only path into the loop was a spreadsheet import, so a
 * manually created prospect sat inert with no way to act on it.
 */
export default function ProspectFlowCard({ p }: { p: ProspectDetail }) {
  const queryClient = useQueryClient();
  const [runNowCoolingDown, setRunNowCoolingDown] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    if (!runNowCoolingDown) return;
    const t = window.setTimeout(() => setRunNowCoolingDown(false), RUN_NOW_COOLDOWN_MS);
    return () => window.clearTimeout(t);
  }, [runNowCoolingDown]);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["prospect", p.id] });
    queryClient.invalidateQueries({ queryKey: ["prospect-threads", p.id] });
    queryClient.invalidateQueries({ queryKey: ["emails"] });
    queryClient.invalidateQueries({ queryKey: ["emails-pending"] });
  }

  /**
   * `run-now` is its own route rather than a second `start`: starting is
   * idempotent and returns early on a prospect that is already running, which
   * is right for a double-clicked "start" and made "Run now" a button that did
   * nothing and reported success while it did it.
   *
   * The tick itself runs after the response, so a queued step is followed by
   * delayed refetches that pick up the message it composed.
   */
  const act = useMutation({
    mutationFn: (action: FlowAction) => api.post<FlowView>(`/api/v1/prospects/${p.id}/${action}`),
    onSuccess: (view, action) => {
      invalidate();
      if (action === "run-now") setRunNowCoolingDown(true);
      if (view.tickDisposition === "QUEUED" && !view.deferredBy) {
        for (const delay of REFRESH_AFTER_TICK_MS) {
          timers.current.push(window.setTimeout(invalidate, delay));
        }
      }
    },
  });

  const runNowResult = act.isSuccess && act.variables === "run-now" ? act.data : null;
  const runNowPending = act.isPending && act.variables === "run-now";

  const setMode = useMutation({
    mutationFn: (approvalMode: ApprovalMode | null) =>
      api.put<FlowView>(`/api/v1/prospects/${p.id}/approval-mode`, { approvalMode }),
    onSuccess: invalidate,
  });

  const unsubscribed = !!p.unsubscribedAt;
  const busy = act.isPending || setMode.isPending;

  return (
    <Card>
      {/* The state badge itself lives in the page header, beside the
          prospect's status — this card is what you do about it. */}
      <CardTitle>Agent flow</CardTitle>

      {unsubscribed ? (
        <p className="text-sm text-[var(--fg-muted)]">
          This prospect has unsubscribed. Outreach is permanently disabled for them.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2">
            {p.flowState === "NOT_STARTED" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => act.mutate("start")}
                className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Play className="h-4 w-4" aria-hidden />
                {act.isPending ? "Starting…" : "Start flow"}
              </button>
            ) : null}

            {p.flowState === "RUNNING" ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => act.mutate("pause")}
                  className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
                >
                  <Pause className="h-4 w-4" aria-hidden /> Pause
                </button>
                <button
                  type="button"
                  disabled={busy || runNowCoolingDown}
                  onClick={() => act.mutate("run-now")}
                  title="Run one agent step now instead of waiting for the schedule"
                  className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
                >
                  {runNowPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Queuing…
                    </>
                  ) : runNowCoolingDown ? (
                    <>
                      <Check className="h-4 w-4" aria-hidden /> Queued
                    </>
                  ) : (
                    <>
                      <Zap className="h-4 w-4" aria-hidden /> Run now
                    </>
                  )}
                </button>
              </>
            ) : null}

            {p.flowState === "PAUSED" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => act.mutate("resume")}
                className="flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              >
                <Play className="h-4 w-4" aria-hidden /> Resume
              </button>
            ) : null}

            {p.flowState === "STOPPED" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => act.mutate("start")}
                className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
              >
                <Play className="h-4 w-4" aria-hidden /> Start again
              </button>
            ) : null}

            {p.flowState !== "STOPPED" && p.flowState !== "NOT_STARTED" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => act.mutate("stop")}
                className="flex items-center gap-1 rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-zinc-50 disabled:opacity-50"
              >
                <Square className="h-4 w-4" aria-hidden /> Stop
              </button>
            ) : null}
          </div>

          {act.error ? (
            <div role="alert" className="mt-2 text-sm text-[var(--danger)]">
              {act.error instanceof ApiError && act.error.status === 409
                ? "That is not possible in this prospect's current state."
                : "That action failed."}
            </div>
          ) : null}

          {runNowResult ? <RunNowNotice view={runNowResult} /> : null}

          {/* "Next action" lives in the page header, next to phase and last
              activity — this card is the controls and the run history. */}
          <dl className="mt-3 space-y-1 text-sm">
            <Row k="Started" v={formatDateTime(p.startedAt)} />
            {p.pausedAt ? <Row k="Paused" v={formatDateTime(p.pausedAt)} /> : null}
          </dl>

          <div className="mt-4 border-t border-[var(--border)] pt-3">
            <label className="block text-xs text-[var(--fg-muted)]" htmlFor="approval-mode">
              Sending mode
            </label>
            <select
              id="approval-mode"
              value={p.approvalMode ?? "INHERIT"}
              disabled={busy}
              onChange={(e) =>
                setMode.mutate(e.target.value === "INHERIT" ? null : (e.target.value as ApprovalMode))
              }
              className="mt-1 w-full rounded-md border border-[var(--border)] p-1.5 text-sm"
            >
              <option value="INHERIT">
                Use strategy setting (currently{" "}
                {p.effectiveApprovalMode === "REVIEW" ? "review first" : "send automatically"})
              </option>
              <option value="REVIEW">Review every message before it sends</option>
              <option value="AUTO">Send automatically, no review</option>
            </select>
            <p className="mt-1 text-xs text-[var(--fg-muted)]">
              {p.effectiveApprovalMode === "REVIEW"
                ? "The agent will compose each message and wait for you to approve it."
                : "The agent will compose and send on its own schedule."}
            </p>
          </div>
        </>
      )}
    </Card>
  );
}

/**
 * What the step the operator just asked for is going to do. The tick honours
 * every gate, so "queued" alone would be the same silence as before — a step
 * queued behind a closed send window looks identical to one that never ran.
 */
function RunNowNotice({ view }: { view: FlowView }) {
  const warn = "mt-2 rounded-md bg-amber-50 p-2 text-sm text-amber-900";
  const good = "mt-2 rounded-md bg-emerald-50 p-2 text-sm text-emerald-900";

  if (view.deferredBy) {
    const because =
      view.deferredBy === "SEND_WINDOW"
        ? "the send window is closed"
        : "the sending limit has been reached";
    return (
      <p role="status" className={warn}>
        Step queued, but {because}. The agent will take it on{" "}
        {formatDateTime(view.deferredUntil)}.
      </p>
    );
  }
  if (view.tickDisposition === "ALREADY_RUNNING") {
    return (
      <p role="status" className={good}>
        A step is already in progress for this prospect. The page refreshes when it finishes.
      </p>
    );
  }
  return (
    <p role="status" className={good}>
      Running one step now. The conversation refreshes in a few seconds.
    </p>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-2">
      <dt className="text-[var(--fg-muted)]">{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}
